import * as THREE from 'three'

export const DEFAULT_UNIT_SCALE = 0.001 // mm³ → cm³
export const DEFAULT_LINEAR_SCALE = 0.1 // mm → cm

export interface ModelMeasurements {
  volume: number
  surfaceArea: number
  dimensions: { x: number; y: number; z: number }
  boundingVolume: number
}

export const DENSITY_TABLE: Record<string, number> = {
  'steel': 7.85,
  'stainless': 7.93,
  'aluminum': 2.70,
  'titanium': 4.43,
  'copper': 8.96,
  'brass': 8.50,
  'cast_iron': 7.20,
  'zinc': 7.14,
  'nylon': 1.15,
  'abs': 1.04,
  'pla': 1.24,
  'pom': 1.41,
  'acrylic': 1.18,
  'pc': 1.20,
  'oak': 0.75,
  'pine': 0.50,
  'glass': 2.50,
  'ceramic': 2.40,
}

export function computeMeasurements(group: THREE.Group): ModelMeasurements {
  let volume = 0
  let surfaceArea = 0
  const box = new THREE.Box3()

  group.traverse(child => {
    if (child instanceof THREE.Mesh && child.geometry) {
      const geo = child.geometry
      const pos = geo.attributes.position
      if (!pos) return

      const positions = pos.array as Float32Array
      const index = geo.index

      if (index) {
        const indices = index.array as (Uint16Array | Uint32Array)
        for (let i = 0; i < indices.length; i += 3) {
          const a = indices[i] * 3
          const b = indices[i + 1] * 3
          const c = indices[i + 2] * 3
          const ax = positions[a], ay = positions[a + 1], az = positions[a + 2]
          const bx = positions[b], by = positions[b + 1], bz = positions[b + 2]
          const cx = positions[c], cy = positions[c + 1], cz = positions[c + 2]
          volume += (
            ax * (by * cz - bz * cy) +
            bx * (cy * az - cz * ay) +
            cx * (ay * bz - az * by)
          ) / 6

          const ux = bx - ax, uy = by - ay, uz = bz - az
          const vx = cx - ax, vy = cy - ay, vz = cz - az
          surfaceArea += 0.5 * Math.hypot(
            uy * vz - uz * vy,
            uz * vx - ux * vz,
            ux * vy - uy * vx,
          )
        }
      } else {
        for (let i = 0; i < positions.length; i += 9) {
          const ax = positions[i], ay = positions[i + 1], az = positions[i + 2]
          const bx = positions[i + 3], by = positions[i + 4], bz = positions[i + 5]
          const cx = positions[i + 6], cy = positions[i + 7], cz = positions[i + 8]
          volume += (
            ax * (by * cz - bz * cy) +
            bx * (cy * az - cz * ay) +
            cx * (ay * bz - az * by)
          ) / 6

          const ux = bx - ax, uy = by - ay, uz = bz - az
          const vx = cx - ax, vy = cy - ay, vz = cz - az
          surfaceArea += 0.5 * Math.hypot(
            uy * vz - uz * vy,
            uz * vx - ux * vz,
            ux * vy - uy * vx,
          )
        }
      }

      box.expandByObject(child)
    }
  })

  volume = Math.abs(volume)
  const size = box.getSize(new THREE.Vector3())

  return {
    volume,
    surfaceArea,
    dimensions: { x: size.x, y: size.y, z: size.z },
    boundingVolume: size.x * size.y * size.z,
  }
}

export function estimateWeight(volume: number, density: number): number {
  return volume * density
}

export type UnitSystem = 'mm' | 'cm' | 'm'

const UNIT_TO_CM: Record<UnitSystem, number> = { mm: 0.1, cm: 1, m: 100 }
const UNIT_TO_CM3: Record<UnitSystem, number> = { mm: 0.001, cm: 1, m: 1_000_000 }
const UNIT_TO_CM2: Record<UnitSystem, number> = { mm: 0.01, cm: 1, m: 10_000 }

export function guessUnitFromFormat(format: string): UnitSystem {
  if (format === 'stl' || format === 'step' || format === 'iges' || format === 'brep') return 'mm'
  if (format === 'gltf' || format === 'glb') return 'm'
  if (format === 'ply' || format === 'obj') return 'mm'
  if (format === 'fbx') return 'cm'
  return 'cm'
}

export function volumeToCm3(volume: number, unit: UnitSystem): number {
  return volume * UNIT_TO_CM3[unit]
}

export function dimToCm(value: number, unit: UnitSystem): number {
  return value * UNIT_TO_CM[unit]
}

export function saToCm2(sa: number, unit: UnitSystem): number {
  return sa * UNIT_TO_CM2[unit]
}

export function hollowVolumeCm3(sa: number, thicknessMm: number, unit: UnitSystem): number {
  const saCm2 = saToCm2(sa, unit)
  const thicknessCm = thicknessMm * 0.1
  return saCm2 * thicknessCm
}

export function formatVolume(volume: number, unit: UnitSystem = 'cm'): string {
  const cm3 = volumeToCm3(volume, unit)
  if (cm3 >= 1000000) return `${(cm3 / 1000000).toFixed(2)} m³`
  if (cm3 >= 1) return `${cm3.toFixed(2)} cm³`
  return `${(cm3 * 1000).toFixed(2)} mm³`
}

export function formatWeight(weight: number): string {
  if (weight >= 1000) return `${(weight / 1000).toFixed(2)} kg`
  if (weight >= 1) return `${weight.toFixed(1)} g`
  return `${(weight * 1000).toFixed(1)} mg`
}

export function formatDimensions(dim: { x: number; y: number; z: number }, unit: UnitSystem = 'cm'): string {
  const fmt = (v: number) => v >= 10 ? v.toFixed(1) : v.toFixed(2)
  const d = { x: dimToCm(dim.x, unit), y: dimToCm(dim.y, unit), z: dimToCm(dim.z, unit) }
  return `${fmt(d.x)} × ${fmt(d.y)} × ${fmt(d.z)}`
}

export function formatSurfaceArea(sa: number, unit: UnitSystem = 'cm'): string {
  const cm2 = saToCm2(sa, unit)
  if (cm2 >= 10000) return `${(cm2 / 10000).toFixed(2)} m²`
  if (cm2 >= 1) return `${cm2.toFixed(2)} cm²`
  return `${(cm2 * 100).toFixed(2)} mm²`
}
